<template>
  <WCModal position="center" @dismiss="dismiss(MODAL_EVENTS.DISMISS)">
    <WCModalHeader class="align-items-baseline">
      <div class="container pl-0 pr-0">
        <div class="row justify-content-between">
          <div class="col-8 align-self-center">
            <h1 class="font-size-lg pt-1">
              {{ $t('customizeItem', { itemName: item.name }) }}
            </h1>
          </div>
          <div class="wc-close-width col-md-auto">
            <button
              @click="dismiss(MODAL_EVENTS.CANCEL)"
              class="btn btn-link text-muted underline-link-from-center"
            >
              {{ $t('cancel') }} <font-awesome-icon icon="xmark" size="lg" class="ml-2" />
            </button>
          </div>
        </div>
        <WCProductPricing
          class="text-left font-size-base font-weight-bold wc-price-width"
          :item="item"
        ></WCProductPricing>
      </div>
    </WCModalHeader>

    <!-- START: body -->
    <WCModalBody>
      <div>
        <div class="container font-weight-bold font-size-xl pl-0 pr-0">
          <div class="row">
            <div class="col-md-auto text-center">
              {{ $t('quantity') }}
              <WCSimpleReactiveQuantityAdjustor
                class="border border-primary mb-3 wc-quantity-width rounded mt-2"
                v-model="randomWeightItemWeightedQuantity"
                :displayValue="displayRandomWeightedQuantity"
                :initialValue="minimumRandomWeightedQuantity"
                :hasRemove="false"
                :incrementStep="randomWeightedQuantityIncrement"
                :maxQty="noMax"
                :minQty="minimumRandomWeightedQuantity"
                :isNextIncrementValid="isNextRandomWeightedQuantityIncrementValid"
                :isNextDecrementValid="isNextRandomWeightedQuantityDecrementValid"
                data-testid="quantityAdjuster"
              >
              </WCSimpleReactiveQuantityAdjustor>
            </div>
            <div class="col-sm-1 pt-4 mt-3">
              <font-awesome-icon icon="xmark" size="sm" />
            </div>
            <div class="col-md-auto text-center">
              {{ $t('weight') }}
              <WCSimpleReactiveQuantityAdjustor
                class="border border-primary mb-3 wc-quantity-width rounded mt-2"
                v-model="randomWeightItemQuantity"
                :displayValue="displayItemQuantity"
                :initialValue="minimumItemQuantity"
                :hasRemove="false"
                :incrementStep="randomItemIncrement"
                :maxQty="noMax"
                :minQty="minimumItemQuantity"
                :isNextIncrementValid="isNextItemQuantityIncrementValid"
                :isNextDecrementValid="isNextItemQuantityDecrementValid"
                data-testid="weightAdjuster"
              >
              </WCSimpleReactiveQuantityAdjustor>
            </div>
            <div class="col-2 pt-4 mt-3 font-size-base">
              <div class="pt-1">
                {{ $t('eachForwardSlash') }}
              </div>
            </div>
          </div>
          <div class="font-weight-normal font-size-lg pt-2">
            <div class="row">
              <div class="col-3 offset-6 font-weight-bold text-right">
                {{ $t('total') }} {{ ': ' }}
              </div>
              <div class="col-3 text-center" data-testid="totalSelectedWeight">
                {{ totalWeight }} {{ weightUnit }}
              </div>
            </div>
            <div class="row text-center">
              <div class="col-3 offset-9" data-testid="totalSelectedPrice">
                {{ $filters.currency(extendedPrice) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </WCModalBody>
    <!-- END: body -->

    <WCModalFooter>
      <div class="container">
        <div class="row">
          <div class="col-7 align-self-center">
            <WCCurrentOrderTypeBadge
              v-if="orderType"
              :orderType="orderType"
              data-testid="orderTypeBadge"
            />
          </div>
          <div class="col-5">
            <button
              @click="addToCartAndClose()"
              class="btn btn-primary btn-block d-flex justify-content-center align-items-center"
              :aria-label="$t('addToCart')"
              data-testid="addToCart"
            >
              <span class="mr-2"> {{ $t('addToCart') }}</span>
              <font-awesome-icon icon="cart-shopping" size="sm" />
            </button>
          </div>
        </div>
      </div>
    </WCModalFooter>
  </WCModal>
</template>

<script lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ModalMixin from '@/modules/modals/mixins/ModalMixin';
import WCModal from '@/modules/modals/components/WCModal/WCModal.vue';
import WCModalHeader from '@/modules/modals/components/WCModalHeader/WCModalHeader.vue';
import WCModalBody from '@/modules/modals/components/WCModalBody/WCModalBody.vue';
import WCModalFooter from '@/modules/modals/components/WCModalFooter/WCModalFooter.vue';
import { MODAL_EVENTS } from '@/constants/EventConstants';
import WCSimpleReactiveQuantityAdjustor from '@/components/WCSimpleReactiveQuantityAdjuster/WCSimpleReactiveQuantityAdjuster.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import WCCurrentOrderTypeBadge from '@/modules/orderTypes/components/WCCurrentOrderTypeBadge/WCCurrentOrderTypeBadge.vue';
import { defineComponent } from 'vue';
import WCProductPricing from '@/components/WCProductPricing/WCProductPricing.vue';
import RandomWeightMixin from './mixins/RandomWeightMixin';

export default defineComponent({
  mixins: [ModalMixin, RandomWeightMixin],
  props: {
    item: {
      type: Object,
      required: true,
    },
    orderType: {
      type: Object,
    },
  },
  data() {
    return {
      MODAL_EVENTS,
    };
  },
  computed: {
    extendedPrice() {
      return this.totalWeight * (this.item.actualPrice / this.item.actualPriceDivider);
    },
    totalWeight() {
      return this.itemQuantityTracker * this.itemWeightedQuantityTracker;
    },
    weightUnit() {
      return this.item.weightProfile.abbrv;
    },
  },
  methods: {
    addToCartAndClose() {
      this.dismiss(MODAL_EVENTS.DISMISS);
      this.addCurrentItemToCart();
    },
    async addCurrentItemToCart() {
      try {
        await this.setQuantity(
          this.itemQuantityTracker,
          this.itemWeightedQuantityTracker,
          this.item,
          '',
          null,
        );
      } catch (error) {
        console.error(error);
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: this.$t('cannotAddItemToCart'),
            message: this.$t('cannotAddItemToCart'),
          },
          actions: {},
          timeout: 5000,
          skipHash: false,
        });
      }
    },
  },
  components: {
    WCProductPricing,
    FontAwesomeIcon,
    WCCurrentOrderTypeBadge,
    WCSimpleReactiveQuantityAdjustor,
    WCModal,
    WCModalHeader,
    WCModalBody,
    WCModalFooter,
  },
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-quantity-width {
  width: 8.5rem;
}
.wc-close-width {
  min-width: 7rem;
}
.wc-price-width {
  width: fit-content;
}
</style>
